import React from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTriangleExclamation } from '@fortawesome/pro-solid-svg-icons/faTriangleExclamation';
import { richTextChildren } from '../../../../../common/richTextChildren';
import { TypedMemo } from '../../../../../common/types';
import { platformName } from '../../../platformMeta';
import { isSocialPlatform } from '../../../types';
import { reelsPlanMessages } from '../reelsPlan/reelsPlanMessages';
import { reelsStepWait, type ReelsStepWait } from '../reelsPlan/reelsPlanModel';
import { planAssetName } from '../rolloutDayCopy';
import { planAssetLabel } from './planAssetLabel';
import { useRollout } from './RolloutContext';
import type { PlanStepView } from './rolloutModel';
import { rolloutMessages } from './rolloutMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the run sheet; each part uses its own slice of it */
import styles from './Rollout.module.scss';
/* eslint-enable css-modules/no-unused-class */

const bold = (chunks: React.ReactNode[]) => <b>{chunks}</b>;

/** The parts of a rich message as fixed children, so React gives no key warning (QA imp-12, lit-22). */
const keyed = richTextChildren;

function nameOf(platform: string): string {
  return isSocialPlatform(platform) ? platformName(platform) : platform;
}

/** "May" of an ISO time (the quota month, UTC). */
function monthName(intl: ReturnType<typeof useIntl>, month: string): string {
  return intl.formatDate(new Date(`${month}-01T00:00:00Z`), { month: 'long', timeZone: 'UTC' });
}

/** The warn line of a step whose asset is missing (design a1). */
function missingLine(intl: ReturnType<typeof useIntl>, step: PlanStepView, title: string): React.ReactNode {
  const { key, day } = step;
  if (key === 'teaser') {
    return intl.formatMessage(rolloutMessages.needsReel, { title, b: bold });
  }
  if (key === 'secondReel' || day.asset?.themeId === 'aiReel') {
    return intl.formatMessage(rolloutMessages.needsSecondReel, { b: bold });
  }
  if (key === 'countdown7') {
    return intl.formatMessage(rolloutMessages.needsCountdowns, { b: bold });
  }
  if (key === 'countdown3' || key === 'countdown1') {
    return null;
  }
  return intl.formatMessage(rolloutMessages.needsKitAsset, {
    asset: planAssetName(intl, day.asset?.themeId ?? ''),
    b: bold,
  });
}

/**
 * The line of a step of an AI reels plan with no ready reel (design f3): queued, made with its
 * percent, or failed. `reelsStepWait` holds the rule.
 */
function reelsStepLine(intl: ReturnType<typeof useIntl>, step: PlanStepView, wait: ReelsStepWait): React.ReactNode {
  if (wait === 'failed') {
    return (
      <p key="reelFailed" className={clsx(styles.line, styles.lineBad)}>
        {intl.formatMessage(reelsPlanMessages.stepFailed)}
      </p>
    );
  }
  return (
    <p key="reelMaking" className={styles.line}>
      {wait === 'making'
        ? intl.formatMessage(reelsPlanMessages.stepMaking, { percent: step.day.asset?.making ?? 0 })
        : intl.formatMessage(reelsPlanMessages.stepQueued)}
    </p>
  );
}

interface StepLinesProps {
  step: PlanStepView;
}

/** The lines under the name of a step (design rollout-plan a1, b1, e1): what it uses, what it needs, what failed. */
const StepLinesComponent: React.FC<StepLinesProps> = ({ step }) => {
  const intl = useIntl();
  const { item, fits, plan, channels: usable } = useRollout();
  const { day, channels } = step;
  const lines: React.ReactNode[] = [];
  const unplanned = channels.every((channel) => channel.state === 'notPlanned');

  const reelsWait = plan.kind === 'reels' ? reelsStepWait(step) : null;
  if (reelsWait !== null) {
    lines.push(reelsStepLine(intl, step, reelsWait));
  } else if (step.assetState === 'missing' && unplanned) {
    const missing = missingLine(intl, step, item.title);
    if (missing !== null) {
      lines.push(
        <p key="missing" className={clsx(styles.line, styles.lineWarn)}>
          <FontAwesomeIcon icon={faTriangleExclamation} />
          {keyed(missing)}
        </p>
      );
    } else {
      lines.push(
        <p key="same" className={styles.line}>
          {intl.formatMessage(rolloutMessages.sameCountdownRun)}
        </p>
      );
    }
  } else if (step.assetState === 'working') {
    lines.push(
      <p key="making" className={styles.line}>
        {intl.formatMessage(rolloutMessages.making, { percent: day.asset?.making ?? 0 })}{' '}
        {day.autoDraft === true && intl.formatMessage(rolloutMessages.makingFills)}
      </p>
    );
  } else if (unplanned && day.asset !== null && step.assetState === 'ready') {
    // The name in the app language, not the English label of the API (QA lit-15).
    const assetLabel = planAssetLabel(intl, day.asset);
    lines.push(
      <p key="uses" className={styles.line}>
        {assetLabel === null
          ? intl.formatMessage(rolloutMessages.usesFile)
          : keyed(intl.formatMessage(rolloutMessages.usesAsset, { label: assetLabel, b: bold }))}
      </p>
    );
    if (usable.length === 0 && plan.kind !== 'reels') {
      // QA 2026-10-28 (imp-1): a draft needs a place to post. Say it before the click, not after.
      lines.push(
        <p key="noChannels" className={clsx(styles.line, styles.lineWarn)}>
          <FontAwesomeIcon icon={faTriangleExclamation} />
          {intl.formatMessage(rolloutMessages.noChannelsStep)}
        </p>
      );
    }
  }

  const drafts = channels.filter((channel) => channel.state === 'draft');
  const unfit = drafts.find((channel) => channel.post !== null && fits.get(channel.post.uuid)?.fits === false);
  if (unfit !== undefined && unfit.post !== null) {
    const month = monthName(intl, fits.get(unfit.post.uuid)?.month ?? unfit.at.slice(0, 7));
    lines.push(
      <p key="fit" className={clsx(styles.line, styles.lineWarn)}>
        <FontAwesomeIcon icon={faTriangleExclamation} />
        {keyed(intl.formatMessage(rolloutMessages.doesNotFit, { month, b: bold }))}
      </p>
    );
  } else if (drafts.length > 0 && drafts.length < channels.length) {
    lines.push(
      <p key="draft" className={styles.line}>
        {intl.formatMessage(rolloutMessages.draftLine, {
          names: drafts.map((channel) => nameOf(channel.platform)).join(', '),
          count: drafts.length,
        })}
      </p>
    );
  }

  channels
    .filter((channel) => channel.state === 'failed')
    .forEach((channel) =>
      lines.push(
        <p key={`failed-${channel.platform}`} className={clsx(styles.line, styles.lineBad)}>
          {intl.formatMessage(rolloutMessages.failedLine, {
            name: nameOf(channel.platform),
            reason: channel.post?.failReason ?? '',
          })}
        </p>
      )
    );

  return <>{lines}</>;
};

export const StepLines = TypedMemo(StepLinesComponent);
